/**
 * `/buddy`'s address: which conversation is open, who a new plan is built for, and which session's
 * panel is out.
 *
 *   ?rozmowa=<id>   a conversation from the history, opened by its id. Absent: the conversation
 *                   this device is in for the recipient (its draft key), or a new one.
 *   ?klient=<id>    the client a NEW conversation's plan is built for (a client's „Plan z Buddym").
 *   ?sesja=<token>  the session whose programming panel is open, by the day's slot token, and
 *   ?wersja=<id>    the draft it belongs to (the build turn), always written with it.
 *   ?szkic=<id>     a draft (the build turn) whose name outside the catalogue opens in its list,
 *   ?nazwa=<name>   by the name as Buddy wrote it; always written together. The builder's panel
 *                   hands a name over this way, since only this page writes the catalogue.
 *
 * Every value is validated here and an unreadable one is dropped rather than kept as noise.
 */

export interface AiPlanBuilderSearch {
  readonly klient?: number;
  readonly rozmowa?: number;
  readonly sesja?: string;
  readonly wersja?: number;
  readonly szkic?: number;
  readonly nazwa?: string;
}

/** The wire's ids are 32-bit: a larger value names no client, conversation or turn. */
const INT32_MAX = 2_147_483_647;

function positiveInteger(raw: unknown): number | undefined {
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 && value <= INT32_MAX ? value : undefined;
}

/** The wire's own ceiling for a slot token: a longer value cannot name any day a draft holds. */
const TOKEN_MAX_LENGTH = 64;

function slotToken(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const token = raw.trim();
  return token.length > 0 && token.length <= TOKEN_MAX_LENGTH ? token : undefined;
}

/** A name longer than this is not one a draft lists, and is not worth an address. */
const NAME_MAX_LENGTH = 1000;

function writtenName(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  return raw.trim().length > 0 && raw.length <= NAME_MAX_LENGTH ? raw : undefined;
}

export function validateAiPlanBuilderSearch(raw: Record<string, unknown>): AiPlanBuilderSearch {
  const klient = positiveInteger(raw.klient);
  const rozmowa = positiveInteger(raw.rozmowa);
  const wersja = positiveInteger(raw.wersja);
  const sesja = slotToken(raw.sesja);
  const szkic = positiveInteger(raw.szkic);
  const nazwa = writtenName(raw.nazwa);
  return {
    ...(klient === undefined ? {} : { klient }),
    ...(rozmowa === undefined ? {} : { rozmowa }),
    ...(sesja === undefined ? {} : { sesja }),
    ...(sesja === undefined || wersja === undefined ? {} : { wersja }),
    ...(szkic === undefined || nazwa === undefined ? {} : { szkic, nazwa }),
  };
}
